import React from 'react';
import PageHero from '../components/shared/PageHero';
import { Shield, Lock, Eye, Mail, FileText, Database, Users, RefreshCw } from 'lucide-react';

const sections = [
  {
    icon: FileText,
    title: 'Information We Collect',
    body: 'We collect information you provide directly to us, such as when you fill out a form on our website, create an account, or contact customer support. This may include your name, email address, phone number, mailing address, date of birth, and financial information necessary to match you with appropriate service providers.'
  },
  {
    icon: Database,
    title: 'How We Use Your Information',
    body: 'We use the information collected to connect you with our network of financial partners, respond to your inquiries, personalize your experience, improve our services, send administrative and marketing communications (with your consent), and comply with legal obligations.'
  },
  {
    icon: Users,
    title: 'Information Sharing',
    body: 'We share your information with our trusted financial partners only when you specifically request to connect with a service. We do not sell your personal information to third parties. We may share information with service providers who assist us in operating our website and services, always under strict confidentiality obligations.'
  },
  {
    icon: Lock,
    title: 'Data Security',
    body: 'We use 256-bit SSL encryption for all data transmission, secure encrypted databases for storage, regular security audits, multi-factor authentication, and strict access controls. While no method of transmission over the Internet is 100% secure, we strive to use commercially acceptable means to protect your information.'
  },
  {
    icon: Eye,
    title: 'Your Privacy Rights',
    body: 'Depending on your location, you may have the right to access, correct, delete, or restrict the use of your personal information. You can opt out of marketing communications at any time. To exercise these rights, please contact us at info@theloanalliance.com.'
  },
  {
    icon: RefreshCw,
    title: 'Changes to This Policy',
    body: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the “Last Updated” date. Continued use of our services after changes constitutes acceptance of the updated policy.'
  }
];

export default function PrivacyPolicy() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title={<>Privacy <span className="text-emerald-600">Policy</span></>}
        subtitle="Your privacy is important to us. This policy describes how we collect, use, and protect your personal information."
        ctaTo=""
      />

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-6 mb-10 flex items-start gap-4">
            <Shield className="w-6 h-6 text-emerald-700 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-emerald-900">
                <span className="font-semibold">Effective Date:</span> July 1, 2025. This Privacy Policy applies to information collected through theloanalliance.com and related services.
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {sections.map((s) => (
              <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 hover:border-emerald-300 hover:shadow-sm transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <h3 className="font-serif-display text-xl font-bold text-slate-900">{s.title}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-slate-900 text-white p-8">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-5 h-5 text-emerald-400" />
              <h3 className="font-serif-display text-xl font-bold">Questions?</h3>
            </div>
            <p className="text-slate-300">If you have any questions about this Privacy Policy or our data practices, please contact us at <a href="mailto:info@theloanalliance.com" className="text-emerald-400 hover:text-emerald-300 underline">info@theloanalliance.com</a>.</p>
          </div>
        </div>
      </section>
    </>
  );
}
